import styled from 'styled-components';
import { OffersFilter } from '../../dtos';
import Checkbox from './Checkbox';
import { useSelector } from 'react-redux';
import ImageWrapper from '../../../../new-components/ImageWrapper';
import { useCallback, useMemo, useRef, useState } from 'react';
import Searchbar from './Searchbar';
import { useTranslation } from 'next-i18next';
import ClearSelectionChip from './ClearSelectionChip';

interface SelectionDropdownProps {
  selections: OffersFilter[];
  type: 'offer_type' | 'Developers_Search' | 'Areas_search';
  handleClick: (selection: OffersFilter) => void;
  setFilterDropdown?: any;
}

const SelectionDropdown = ({
  selections,
  type,
  handleClick,
  setFilterDropdown
}: SelectionDropdownProps) => {
  const { t } = useTranslation(['cityscape', 'common']);

  const isMobile = useSelector((state: any) => state.app.isMobile);

  const developerIds = useSelector((state: any) => state.offers.developers_ids);
  const areasIds = useSelector((state: any) => state.offers.areas_ids);
  const offersFilters = useSelector(
    (state: any) => state.offers.offers_filters
  );

  const dropdownRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');

  const closeDrawer = () => {
    setFilterDropdown(null);
  };

  const isChecked = useCallback(
    (selection: OffersFilter) => {
      if (selection.key && offersFilters.includes(selection.key)) {
        return true;
      }

      if (selection.id) {
        return (
          (type === 'Areas_search' && areasIds.includes(selection.id)) ||
          (type === 'Developers_Search' && developerIds.includes(selection.id))
        );
      }
    },
    [areasIds, developerIds, offersFilters]
  );

  const renderSelection = useCallback(
    (selection: OffersFilter) => {
      return (
        <div
          key={`${selection.key} ${selection.name}`}
          className="selection-item"
          onClick={() => handleClick(selection)}
        >
          {type === 'Developers_Search' ? (
            <div className="developer-info">
              <div className="logo">
                <ImageWrapper
                  src={selection.logo_path ?? ''}
                  alt="Developer Logo"
                  width={42}
                  height={42}
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '42px',
                    border: '2px solid #d3dbe6',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <span key={selection.key}>{selection.name}</span>
            </div>
          ) : (
            <span>{selection.value ?? selection.name}</span>
          )}

          <Checkbox checked={isChecked(selection)} />
        </div>
      );
    },
    [searchTerm, developerIds, areasIds, offersFilters]
  );

  const selectionsItems = useMemo(
    () =>
      selections.filter((selection: OffersFilter) =>
        type === 'offer_type'
          ? selection.value?.toLowerCase().includes(searchTerm.toLowerCase())
          : selection.name?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm]
  );

  return (
    <Container type={type} className="hide-scrollbar" ref={dropdownRef}>
      <div className="search-bar">
        <Searchbar setSearchTerm={setSearchTerm} type={type} />
      </div>

      {(type === 'Areas_search' && areasIds.length > 0) ||
      (type === 'Developers_Search' && developerIds.length > 0) ||
      (type === 'offer_type' && offersFilters.length > 0) ? (
        <div className="selection-chips hide-scrollbar">
          {selectionsItems
            .filter(selection => {
              return (
                (type === 'Areas_search' && areasIds.includes(selection.id)) ||
                (type === 'Developers_Search' &&
                  developerIds.includes(selection.id)) ||
                (type === 'offer_type' && offersFilters.includes(selection.key))
              );
            })
            .map(selection => (
              <ClearSelectionChip
                key={selection.id}
                id={selection.key || selection.id!}
                label={selection.value! || selection.name!}
                filterType={type}
              />
            ))}
        </div>
      ) : (
        <></>
      )}

      <div className="options-container">
        {selectionsItems.length > 0 ? (
          selectionsItems.map((selection: OffersFilter) =>
            renderSelection(selection)
          )
        ) : (
          <span className="no-results">
            {t('no_results', { ns: 'common' })}
          </span>
        )}
      </div>

      {isMobile && (
        <div className="apply-btn-container">
          <button className="apply-button" onClick={closeDrawer}>
            {t('add')}
          </button>
        </div>
      )}
    </Container>
  );
};

const Container = styled.div<{
  type: 'offer_type' | 'Developers_Search' | 'Areas_search';
}>`
  position: absolute;
  display: flex;
  flex-direction: column;
  text-align: start;
  gap: 16px;
  top: 45px;
  padding: 4px;
  background: #fbfcfe;
  border: 1px solid #d3dbe6;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 99;
  width: 393px;
  overflow-y: auto;
  white-space: nowrap;
  max-height: 400px;
  padding: 0px 16px 24px 16px;

  .search-bar {
    position: sticky;
    top: 0;
    background-color: white;
    height: 60px;
    padding-top: 8px;
    z-index: 999;
  }

  .selection-chips {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 30px;
    overflow-x: scroll;
  }

  .options-container {
    display: flex;
    flex-direction: column;
    gap: ${props => (props.type === 'Developers_Search' ? '4px' : '16px')};
    flex: 1;

    .no-results {
      text-align: center;
    }

    .selection-item {
      cursor: pointer;
      width: 100%;
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      justify-content: start;
      gap: 8px;
      padding-bottom: 8px;
      &:not(:last-child) {
        border-bottom: 1px solid #d3dbe6;
      }

      .developer-info {
        display: flex;
        align-items: center;
        gap: 8px;
        overflow: hidden;

        .logo {
          max-width: 42px;
          max-height: 42px;
        }

        span {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }

    span {
      user-select: none;
      text-align: start;
      padding: 4px 4px;
      border-radius: 4px;
      white-space: nowrap;
      transition: background 0.3s ease;
      color: #5b6573;
      font-family: materialMedium;
      font-size: 14px;
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    position: relative;
    top: unset;
    width: 100%;
    max-height: unset;
    height: 100%;
    box-shadow: none;
    border: none;
    padding: 0;
    padding-bottom: 75px;

    .options-container {
      .selection-item {
        flex-direction: row;
        justify-content: space-between;
      }
    }

    .apply-btn-container {
      position: fixed;
      bottom: 16px;
      left: 0;
      width: 100%;
      height: 55px;
      background-color: #fff;
      padding: 0 16px;

      .apply-button {
        width: 100%;
        height: 55px;
        padding: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 10px;
        border: none;
        background: #015c9a;
        color: #fff;
        font-size: 14px;
        font-family: materialMedium;
      }
    }
  }
`;

export default SelectionDropdown;
